import type React from "react";
import { useState, useEffect, useMemo } from "react";
import { billingApi, type BillingDashboard, type Payment } from "../../services/billingApi";
import { Spinner } from "@cynaps/ui";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, CartesianGrid, ReferenceArea } from "recharts";
import "./CreditDashboard.css";

// SVG Icons
const CreditIcon = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" style={{ filter: "drop-shadow(0 4px 6px rgba(234, 179, 8, 0.25))" }}>
    <defs>
      <linearGradient id="coinGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FDE047" />
        <stop offset="50%" stopColor="#EAB308" />
        <stop offset="100%" stopColor="#A16207" />
      </linearGradient>
      <linearGradient id="coinInner" x1="1" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#FEF08A" />
        <stop offset="100%" stopColor="#CA8A04" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="16" r="14" fill="url(#coinGrad)" />
    <circle cx="16" cy="16" r="10.5" fill="url(#coinInner)" />
    <path d="M19 11.5 A 5.5 5.5 0 1 0 19 20.5" stroke="#FEF9C3" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    <line x1="16" y1="9" x2="16" y2="23" stroke="#FEF9C3" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const RolloverIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 11-3-6.7" />
    <path d="M21 3v6h-6" />
  </svg>
);

const PlanIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#e8e4d9"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 8h10M7 12h6M7 16h8" />
  </svg>
);

const StorageIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#f59e0b"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 11-3-6.7" />
    <path d="M21 3v6h-6" />
  </svg>
);

const CardIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const TagIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <circle cx="7" cy="7" r="1" />
  </svg>
);

const GiftIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="8" width="18" height="14" rx="2" />
    <path d="M12 8V22M3 12h18M12 8c-2 0-4-2-4-4s2-2 4 0c2-2 4-2 4 0s-2 4-4 4z" />
  </svg>
);

const MinusIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

// Simple bezier smoothing function
const smoothPath = (points: [number, number][]) => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0][0]},${points[0][1]}`;

  let path = `M ${points[0][0]},${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const x0 = i ? points[i - 1][0] : points[i][0];
    const y0 = i ? points[i - 1][1] : points[i][1];
    const x1 = points[i][0];
    const y1 = points[i][1];
    const x2 = points[i + 1][0];
    const y2 = points[i + 1][1];
    const x3 = i + 2 < points.length ? points[i + 2][0] : x2;
    const y3 = i + 2 < points.length ? points[i + 2][1] : y2;

    const cp1x = x1 + (x2 - x0) / 6;
    const cp1y = y1 + (y2 - y0) / 6;
    const cp2x = x2 - (x3 - x1) / 6;
    const cp2y = y2 - (y3 - y1) / 6;

    path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${x2},${y2}`;
  }
  return path;
};

// Modern Line Chart Component using Recharts
const LineChart: React.FC<{
  data: { date: string; credits: number; debits: number; netTrend: number }[];
}> = ({ data }) => {
  const [chartData, setChartData] = useState(data);
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);

  useEffect(() => {
    setChartData(data);
  }, [data]);

  const zoom = () => {
    let left = refAreaLeft;
    let right = refAreaRight;

    if (left === right || right === null || left === null) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }

    const leftIndex = data.findIndex(d => d.date === left);
    const rightIndex = data.findIndex(d => d.date === right);

    if (leftIndex > rightIndex) {
      [left, right] = [right, left];
    }

    const newChartData = data.slice(Math.min(leftIndex, rightIndex), Math.max(leftIndex, rightIndex) + 1);

    setChartData(newChartData);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  const zoomOut = () => {
    setChartData(data);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[340px] text-slate-500 font-sans">
        No transaction data available
      </div>
    );
  }

  const formatCreditsNum = (amount: number): string => {
    const num = Math.abs(amount);
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return `${Math.round(num)}`;
  };

  return (
    <div
      className="relative w-full h-[340px] bg-slate-900/20 rounded-2xl border border-white/5 overflow-hidden p-6"
      style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", userSelect: "none" }}
    >
      {chartData.length !== data.length && (
        <button
          onClick={zoomOut}
          title="Zoom Out"
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 10,
            background: "rgba(139, 92, 246, 0.2)",
            border: "1px solid rgba(139, 92, 246, 0.5)",
            color: "#fff",
            padding: "8px",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(139, 92, 246, 0.4)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "rgba(139, 92, 246, 0.2)"}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </button>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart 
          data={chartData} 
          margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
          onMouseDown={(e) => e?.activeLabel && setRefAreaLeft(e.activeLabel)}
          onMouseMove={(e) => refAreaLeft && e?.activeLabel && setRefAreaRight(e.activeLabel)}
          onMouseUp={zoom}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
          <XAxis 
            dataKey="date" 
            stroke="rgba(255, 255, 255, 0.4)" 
            tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickMargin={12}
            minTickGap={30}
          />
          <YAxis 
            yAxisId="left"
            stroke="rgba(255, 255, 255, 0.4)" 
            tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => formatCreditsNum(value)}
            tickMargin={12}
          />
          <YAxis 
            yAxisId="right"
            orientation="right"
            stroke="rgba(255, 255, 255, 0.4)" 
            tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => formatCreditsNum(value)}
            tickMargin={12}
          />
          <RechartsTooltip 
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            contentStyle={{ backgroundColor: "rgba(30, 41, 59, 0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)", backdropFilter: "blur(12px)" }}
            itemStyle={{ color: "#fff", fontWeight: 700, fontSize: "16px" }}
            labelStyle={{ color: "#94a3b8", marginBottom: "6px", fontSize: "12px", fontWeight: 500, letterSpacing: "0.025em" }}
          />
          <Bar yAxisId="left" dataKey="debits" name="Credits Used" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={40} />
          <Bar yAxisId="left" dataKey="credits" name="Credits Added" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
          <Line yAxisId="right" type="monotone" dataKey="netTrend" name="Net Trend" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4, fill: "#8b5cf6", strokeWidth: 2, stroke: "#1e293b" }} activeDot={{ r: 6, fill: "#c084fc", strokeWidth: 0 }} />
          
          {refAreaLeft && refAreaRight ? (
            <ReferenceArea yAxisId="left" x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} fill="#8b5cf6" fillOpacity={0.1} />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};


export const CreditDashboard: React.FC = () => {
  const [dashboard, setDashboard] = useState<BillingDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "transactions" | "payments">("overview");
  const [chartStartDate, setChartStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().split("T")[0];
  });
  const [chartEndDate, setChartEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });

  // Payments Pagination State
  const [paymentsList, setPaymentsList] = useState<Payment[]>([]);
  const [paymentsOffset, setPaymentsOffset] = useState(0);
  const [hasMorePayments, setHasMorePayments] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const PAYMENTS_LIMIT = 15;

  useEffect(() => {
    if (activeTab === "payments" && paymentsList.length === 0) {
      loadMorePayments();
    }
  }, [activeTab]);

  const loadMorePayments = async () => {
    if (loadingPayments || !hasMorePayments) return;

    try {
      setLoadingPayments(true);
      const data = await billingApi.getPayments(undefined, PAYMENTS_LIMIT, paymentsOffset);

      setPaymentsList((prev) => [...prev, ...data.results]);
      setPaymentsOffset((prev) => prev + PAYMENTS_LIMIT);
      setHasMorePayments(data.has_more);
    } catch (error) {
      console.error("Failed to load payments", error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handlePaymentsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMorePayments();
    }
  };

  // Process data for chart
  const chartData = useMemo(() => {
    if (!dashboard) return [];

    // Group transactions by local date string
    const dataByDate = new Map<string, { credits: number; debits: number }>();

    dashboard.recent_transactions.forEach((txn) => {
      const d = new Date(txn.created_at);
      const dateStr = d.toLocaleDateString("en-US");
      const existing = dataByDate.get(dateStr) || { credits: 0, debits: 0 };

      if (txn.transaction_type === "credit") {
        existing.credits += txn.amount;
      } else {
        existing.debits += Math.abs(txn.amount);
      }

      dataByDate.set(dateStr, existing);
    });

    // Generate days explicitly to ensure contiguous X-axis
    const start = new Date(chartStartDate);
    const end = new Date(chartEndDate);
    const days: Date[] = [];
    
    if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end) {
      let current = new Date(start);
      let count = 0;
      while (current <= end && count < 90) { // Limit to 90 days max for performance/UI sanity
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
        count++;
      }
    } else {
      days.push(new Date());
    }

    let cumulativeNet = 0;
    
    const chartPoints = days.map((d) => {
      const dateStr = d.toLocaleDateString("en-US");
      const displayDate = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const values = dataByDate.get(dateStr) || { credits: 0, debits: 0 };
      
      const net = values.credits - values.debits;
      cumulativeNet += net;
      
      return {
        date: displayDate,
        credits: values.credits,
        debits: values.debits,
        netTrend: cumulativeNet
      };
    });

    return chartPoints;
  }, [dashboard, chartStartDate, chartEndDate]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await billingApi.getDashboard();
      setDashboard(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load billing dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const CreditsDisplay: React.FC<{ amount: number }> = ({ amount }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
      {amount.toLocaleString("en-US")}
      <CreditIcon size={14} />
    </span>
  );

  const getTransactionIcon = (type: string, category: string) => {
    if (type === "credit") {
      if (category === "purchase" || category === "subscription") return <CardIcon />;
      if (category === "bonus") return <GiftIcon />;
      return <PlusIcon />;
    }
    if (category === "annotation") return <TagIcon />;
    return <MinusIcon />;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      pending: { color: "#f59e0b", label: "Pending" },
      authorized: { color: "#3b82f6", label: "Authorized" },
      captured: { color: "#10b981", label: "Completed" },
      refunded: { color: "#6b7280", label: "Refunded" },
      failed: { color: "#ef4444", label: "Failed" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className="status-badge" style={{ backgroundColor: config.color }}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="credit-dashboard">
        <div className="dashboard-loading">
          <Spinner size={64} />
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="credit-dashboard">
        <div className="dashboard-error">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={loadDashboard}>Retry</button>
        </div>
      </div>
    );
  }

  const { billing, recent_transactions, recent_payments } = dashboard;

  return (
    <div className="credit-dashboard">
      <div className="dashboard-header">
        <h1>Billing Dashboard</h1>
        <button className="refresh-btn" onClick={loadDashboard}>
          <RefreshIcon /> Refresh
        </button>
      </div>

      {/* Credit Balance Cards */}
      <div className="balance-cards">
        <div className="balance-card primary">
          <div className="card-icon">
            <CreditIcon />
          </div>
          <div className="card-content">
            <div className="card-label">Available Credits</div>
            <div className="card-value">{billing.available_credits.toLocaleString()}</div>
            <div className="card-note">Ready to use</div>
          </div>
        </div>

        <div className="balance-card">
          <div className="card-icon">
            <PlanIcon />
          </div>
          <div className="card-content">
            <div className="card-label">Billing Type</div>
            <div className="card-value">
              {billing.billing_type === "subscription" ? "Subscription" : "Pay As You Go"}
            </div>
            {billing.active_subscription_details && (
              <div className="card-note">{billing.active_subscription_details.plan_details.name}</div>
            )}
          </div>
        </div>

        <div className="balance-card">
          <div className="card-icon">
            <StorageIcon />
          </div>
          <div className="card-content">
            <div className="card-label">Storage Used</div>
            <div className="card-value">{billing.storage_used_gb} GB</div>
            <div className="card-note">
              {billing.active_subscription_details
                ? `${billing.active_subscription_details.plan_details.storage_gb} GB included`
                : "5 GB free"}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Info */}
      {billing.active_subscription_details && (
        <div className="subscription-info">
          <div className="info-header">
            <h3>Active Subscription</h3>
            <span className="subscription-status active">Active</span>
          </div>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Plan:</span>
              <span className="value">{billing.active_subscription_details.plan_details.name}</span>
            </div>
            <div className="info-item">
              <span className="label">Monthly Credits:</span>
              <span className="value">
                {billing.active_subscription_details.plan_details.credits_per_month.toLocaleString()}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Next Billing:</span>
              <span className="value">{formatDate(billing.active_subscription_details.next_billing_date)}</span>
            </div>
            <div className="info-item">
              <span className="label">Auto Renew:</span>
              <span className="value">{billing.active_subscription_details.auto_renew ? "Enabled" : "Disabled"}</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button className={activeTab === "overview" ? "active" : ""} onClick={() => setActiveTab("overview")}>
          Overview
        </button>
        <button className={activeTab === "transactions" ? "active" : ""} onClick={() => setActiveTab("transactions")}>
          Transactions
        </button>
        <button className={activeTab === "payments" ? "active" : ""} onClick={() => setActiveTab("payments")}>
          Payments
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "overview" && (
          <div className="overview-section">
            {/* Chart Section */}
            <div className="chart-section">
              <div className="chart-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <h3
                    style={{
                      fontFamily: "'Space Grotesk', system-ui, sans-serif",
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "var(--color-neutral-content)",
                      margin: 0,
                    }}
                  >
                    Credits Flow
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input 
                      type="date"
                      value={chartStartDate}
                      onChange={(e) => setChartStartDate(e.target.value)}
                      style={{
                        background: "rgba(30, 41, 59, 0.8)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#e8e4d9",
                        borderRadius: "6px",
                        padding: "4px 8px",
                        fontSize: "12px",
                        fontFamily: "'Space Grotesk', system-ui, sans-serif",
                        outline: "none"
                      }}
                    />
                    <span style={{ color: "#6b7280", fontSize: "12px" }}>to</span>
                    <input 
                      type="date"
                      value={chartEndDate}
                      onChange={(e) => setChartEndDate(e.target.value)}
                      style={{
                        background: "rgba(30, 41, 59, 0.8)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#e8e4d9",
                        borderRadius: "6px",
                        padding: "4px 8px",
                        fontSize: "12px",
                        fontFamily: "'Space Grotesk', system-ui, sans-serif",
                        outline: "none"
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "24px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontFamily: "'Space Grotesk', system-ui, sans-serif",
                      fontSize: "13px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "3px",
                        background: "#10b981",
                        borderRadius: "2px",
                        boxShadow: "0 0 8px rgba(16, 185, 129, 0.5)",
                      }}
                    />
                    <span style={{ color: "rgba(255, 255, 255, 0.7)" }}>Credits Added</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontFamily: "'Space Grotesk', system-ui, sans-serif",
                      fontSize: "13px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "3px",
                        background: "#ef4444",
                        borderRadius: "2px",
                        boxShadow: "0 0 8px rgba(239, 68, 68, 0.5)",
                      }}
                    />
                    <span style={{ color: "rgba(255, 255, 255, 0.7)" }}>Credits Used</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontFamily: "'Space Grotesk', system-ui, sans-serif",
                      fontSize: "13px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "3px",
                        background: "#8b5cf6",
                        borderRadius: "2px",
                        boxShadow: "0 0 8px rgba(139, 92, 246, 0.5)",
                      }}
                    />
                    <span style={{ color: "rgba(255, 255, 255, 0.7)" }}>Net Trend</span>
                  </div>
                </div>
              </div>
              <div className="chart-container">
                <LineChart data={chartData} />
              </div>
            </div>

            <div className="section-grid">
              {/* Recent Transactions */}
              <div className="section-card">
                <h3>Recent Transactions</h3>
                {recent_transactions.length === 0 ? (
                  <p className="empty-state">No transactions yet</p>
                ) : (
                  <div className="transaction-list">
                    {recent_transactions.slice(0, 5).map((txn) => (
                      <div key={txn.id} className="transaction-item">
                        <div className="txn-icon">{getTransactionIcon(txn.transaction_type, txn.category)}</div>
                        <div className="txn-details">
                          <div className="txn-description">{txn.description}</div>
                          <div className="txn-date">{formatDate(txn.created_at)}</div>
                        </div>
                        <div className={`txn-amount ${txn.transaction_type}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {txn.transaction_type === "credit" ? "+" : "-"}
                          {Math.abs(txn.amount).toLocaleString()}
                          <CreditIcon size={16} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Payments */}
              <div className="section-card">
                <h3>Recent Payments</h3>
                {recent_payments.length === 0 ? (
                  <p className="empty-state">No payments yet</p>
                ) : (
                  <div className="payment-list">
                    {recent_payments.slice(0, 5).map((payment) => (
                      <div key={payment.id} className="payment-item">
                        <div className="payment-details">
                          <div className="payment-description">
                            {payment.credit_package_details
                              ? payment.credit_package_details.name
                              : "Subscription Payment"}
                          </div>
                          <div className="payment-date">{formatDate(payment.created_at)}</div>
                        </div>
                        <div className="payment-right">
                          <div className="payment-amount">
                            <CreditsDisplay amount={payment.amount_inr} />
                          </div>
                          {getStatusBadge(payment.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "transactions" && (
          <div className="transactions-section">
            <div className="section-card">
              <h3>All Transactions</h3>
              {recent_transactions.length === 0 ? (
                <p className="empty-state">No transactions found</p>
              ) : (
                <div className="transactions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent_transactions.map((txn) => (
                        <tr key={txn.id}>
                          <td>{formatDate(txn.created_at)}</td>
                          <td>{txn.description}</td>
                          <td>
                            <span className={`type-badge ${txn.transaction_type}`}>{txn.transaction_type}</span>
                          </td>
                          <td>{txn.category}</td>
                          <td className={`amount ${txn.transaction_type}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {txn.transaction_type === "credit" ? "+" : "-"}
                              {Math.abs(txn.amount).toLocaleString()}
                              <CreditIcon size={14} />
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {txn.balance_after.toLocaleString()}
                              <CreditIcon size={14} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "payments" && (
          <div className="payments-section">
            <div className="section-card">
              <h3>Payment History</h3>
              {paymentsList.length === 0 && !loadingPayments ? (
                <p className="empty-state">No payments found</p>
              ) : (
                <div
                  className="payments-table"
                  style={{ maxHeight: "600px", overflowY: "auto" }}
                  onScroll={handlePaymentsScroll}
                >
                  <table>
                    <thead>
                      <tr>
                        <th
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "var(--color-neutral-surface)",
                            zIndex: 1,
                          }}
                        >
                          Date
                        </th>
                        <th
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "var(--color-neutral-surface)",
                            zIndex: 1,
                          }}
                        >
                          Description
                        </th>
                        <th
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "var(--color-neutral-surface)",
                            zIndex: 1,
                          }}
                        >
                          Order ID
                        </th>
                        <th
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "var(--color-neutral-surface)",
                            zIndex: 1,
                          }}
                        >
                          Method
                        </th>
                        <th
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "var(--color-neutral-surface)",
                            zIndex: 1,
                          }}
                        >
                          Amount
                        </th>
                        <th
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "var(--color-neutral-surface)",
                            zIndex: 1,
                          }}
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsList.map((payment) => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.created_at)}</td>
                          <td>
                            {payment.credit_package_details ? payment.credit_package_details.name : "Subscription"}
                          </td>
                          <td className="order-id">{payment.razorpay_order_id}</td>
                          <td>{payment.payment_method || "-"}</td>
                          <td className="amount">
                            <CreditsDisplay amount={payment.amount_inr} />
                          </td>
                          <td>{getStatusBadge(payment.status)}</td>
                        </tr>
                      ))}
                      {loadingPayments && (
                        <tr>
                          <td colSpan={6} style={{ textAlign: "center", padding: "20px" }}>
                            <Spinner size={24} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
